import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useStore } from '../../../../store';
import { RoleItemUserRole } from './templates/RoleItemUserRole';
import { RoleItemUserDetails } from './templates/RoleItemUserDetails';
import { RoleItemRolePermissions } from './templates/RoleItemRolePermissions';
import { RoleItemInviteStatus } from './templates/RoleItemInviteStatus';
import { RoleItemRemove } from './templates/RoleItemRemove';
import { ISummaryData, RoleType, TeamMember } from '../../../../types';
import { PermissionsList } from './PermissionsList';

interface AccountSettingsRolesAndPermissionsItemProps {
  team: TeamMember[];
  onRemove: (teamMember: TeamMember) => void;
}

export const AccountSettingsRolesAndPermissionsItem = ({
  onRemove,
  team,
}: AccountSettingsRolesAndPermissionsItemProps) => {
  const headerClassName = 'py-[10px] px-0 font-metricR text-[14px] text-bw07 text-left !bg-bw11';
  const columnClassName = 'py-[12px] p-0';
  const { t } = useTranslation();
  const [permissionsTypeToShow, setPermissionsTypeToShow] = React.useState<RoleType | null>(null);
  const {
    customer: { email },
    authorizedUserData,
  }: ISummaryData = useStore((state) => state.summary);
  const isAuthorizedUser = authorizedUserData !== undefined;
  const currentUserEmail = authorizedUserData?.authorizedUserDetails?.email || email;
  team.sort((a, b) => {
    if (a.email === currentUserEmail) return -1;
    if (b.email === currentUserEmail) return 1;
    return 0;
  });
  const lastUserEmail = team[team.length - 1].email;
  return (
    <div className='flex flex-row border-bw03 border-[1px]  rounded-md   bg-bw01 w-full mt-[18px] mb-[16px]'>
      <DataTable
        id='data-table-prime-react-products'
        value={team}
        className='p-0 m-0 w-full gap-3 '
        rowHover={false}
        cellSelection={false}
        rowClassName={(teamMember: TeamMember) =>
          twMerge(teamMember.email !== lastUserEmail && 'border-bw03 border-b-[1px]')}
      >
        <Column
          headerClassName={`${headerClassName} pl-[16px]`}
          className={`${columnClassName} p-[16px]`}
          header={t('ACCOUNT_SETTINGS.TEAM.TABLE.USER_DETAILS')}
          body={(teamMember) => (
            <RoleItemUserDetails teamMember={teamMember} isCurrentUser={teamMember.isCurrentlyLoggedInUser} />
          )}
        />
        <Column
          headerClassName={headerClassName}
          className={`${columnClassName}  !min-w-[100px]`}
          header={t('ACCOUNT_SETTINGS.TEAM.TABLE.ROLE')}
          body={(teamMember) => <RoleItemUserRole teamMember={teamMember} />}
        />
        <Column
          headerClassName={headerClassName}
          className={`${columnClassName} !min-w-[120px]`}
          header={t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS')}
          body={(teamMember) => (
            <RoleItemRolePermissions
              permission={teamMember.permissionsAccessLevel}
              onInfoClick={(role) => setPermissionsTypeToShow(role)}
              role={teamMember.teamMemberType}
            />
          )}
        />
        <Column
          headerClassName={headerClassName}
          className={`${columnClassName} !min-w-[70px]`}
          header=''
          body={(teamMember) => <RoleItemInviteStatus status={teamMember.invitationStatus} />}
        />
        <Column
          headerClassName={headerClassName}
          className={columnClassName}
          header=''
          body={(teamMember) => (
            <RoleItemRemove
              isAuthorizedUser={isAuthorizedUser}
              onClick={() => onRemove(teamMember)}
              allowRemove={teamMember.isAllowedForRemoval}
            />
          )}
        />
      </DataTable>
      {permissionsTypeToShow && (
        <PermissionsList onClose={() => setPermissionsTypeToShow(null)} type={permissionsTypeToShow} />
      )}
    </div>
  );
};
